/**
 * API pour les services externes (SMS, Navixy, etc.)
 * Utilise les credentials depuis la configuration
 */
import axios from 'axios';
import config from '../config/api';

let cachedSmsToken = null;
let cachedSmsTokenExpiry = 0;

/**
 * Normalise un numéro marocain au format 212XXXXXXXXX attendu par Orange.
 */
export const normalizeSmsRecipient = (phone) => {
  if (!phone) return null;

  let digits = String(phone).replace(/\D/g, '');
  if (!digits) return null;

  if (digits.startsWith('00')) {
    digits = digits.slice(2);
  }

  if (digits.startsWith('0')) {
    digits = `212${digits.slice(1)}`;
  } else if (!digits.startsWith('212')) {
    digits = `212${digits}`;
  }

  return digits;
};

const getOrangeSmsToken = async () => {
  const now = Date.now();
  if (cachedSmsToken && now < cachedSmsTokenExpiry) {
    return cachedSmsToken;
  }

  const { sms } = config;
  const authResponse = await axios.post(
    `${sms.apiUrl}/accounts/users/login`,
    {
      email: sms.email,
      password: sms.password,
    },
    {
      headers: { 'Content-Type': 'application/json' },
      withCredentials: false,
    }
  );

  const token = authResponse.data?.access_token;
  if (!token) {
    throw new Error('Token SMS Orange manquant dans la réponse d\'authentification.');
  }

  cachedSmsToken = token;
  cachedSmsTokenExpiry = now + 50 * 60 * 1000;
  return token;
};

/**
 * Envoie un SMS via l'API Orange SmartMessage.
 */
export const sendSMS = async (phone, message) => {
  try {
    const { sms } = config;

    if (!sms.email || !sms.password) {
      throw new Error('Configuration SMS manquante. Vérifiez REACT_APP_SMS_EMAIL et REACT_APP_SMS_PASSWORD.');
    }

    const recipient = normalizeSmsRecipient(phone);
    if (!recipient) {
      throw new Error('Numéro de téléphone invalide pour l\'envoi SMS.');
    }

    const token = await getOrangeSmsToken();

    const smsResponse = await axios.post(
      `${sms.apiUrl}/sms/send`,
      {
        name: 'Non-Bulk-Campaign',
        recipient,
        msg: message,
        sender: sms.sender,
        category: sms.category,
      },
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        withCredentials: false,
      }
    );

    const smsData = smsResponse.data;
    if (smsData?.statusCode === 0 || smsData?.statusMsg === 'SUCCESS') {
      return smsData;
    }

    throw new Error(smsData?.statusMsg || 'Échec de l\'envoi du SMS Orange.');
  } catch (error) {
    cachedSmsToken = null;
    cachedSmsTokenExpiry = 0;
    console.error('Erreur envoi SMS:', error);
    throw error;
  }
};

/**
 * Récupère la position d'un véhicule via Navixy
 */
export const fetchVehicleLocation = async (trackerId) => {
  try {
    const { navixy } = config;

    if (!navixy.hash) {
      throw new Error('Configuration Navixy manquante. Vérifiez les variables d\'environnement.');
    }

    const data = {
      hash: navixy.hash,
      tracker_id: trackerId,
    };

    const response = await axios.post(
      `${navixy.apiUrl}/tracker/get_state`,
      data,
      {
        headers: { 'Content-Type': 'application/json' },
        withCredentials: false,
      }
    );

    return {
      lat: response.data.state.gps.location.lat,
      lng: response.data.state.gps.location.lng,
    };
  } catch (error) {
    console.error('Error fetching vehicle location:', error);
    return null;
  }
};